import type {
  MarketingAuditResponse,
  MarketingDashboardData,
  MarketingDecisionMatrixResponse,
  MarketingFilterOptionsData,
  MarketingFetchParams,
  MarketingInterestRegionsResponse,
  MarketingOverviewResponse,
  MarketingRoute,
  MarketingRowsResponse,
} from '../../shared/models';
import { HttpRepository } from './HttpRepository';
import { getHabitaMarketingResourceUrl } from './habitaApiUrls';

const marketingRoutes = {
  ads: 'ads',
  ageGender: 'age-gender',
  audit: 'audit',
  campaigns: 'campaigns',
  creativeRegions: 'creative-regions',
  decisionMatrix: 'decision-matrix',
  filterOptions: 'filter-options',
  interestRegions: 'interest-regions',
  overview: 'overview',
  placement: 'placement',
  regions: 'regions',
} as const;

function appendQuery(url: string, params: MarketingFetchParams): string {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });

  const query = search.toString();
  return query ? `${url}${url.includes('?') ? '&' : '?'}${query}` : url;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function normalizeRowsResponse(raw: unknown, route: MarketingRoute): MarketingRowsResponse {
  const record = asRecord(raw);
  const rows = Array.isArray(record.rows) ? record.rows : [];

  return {
    ...(record as Partial<MarketingRowsResponse>),
    ok: record.ok === true,
    resource: String(record.resource ?? route),
    rows: rows.map((row) => asRecord(row)),
  };
}

function normalizeInterestRegionsResponse(raw: unknown): MarketingInterestRegionsResponse {
  const record = asRecord(raw);
  const otherLocalities = rowsFromRecord(record, 'other_localities');

  return {
    ...(record as Partial<MarketingInterestRegionsResponse>),
    ok: record.ok === true,
    other_localities: otherLocalities.map((item) => ({
      name: String(item.name ?? '').trim(),
      occurrences: Number.isFinite(Number(item.occurrences)) ? Number(item.occurrences) : 0,
    })).filter((item) => item.name && item.occurrences > 0),
    resource: String(record.resource ?? 'interest_regions'),
    rows: rowsFromRecord(record, 'rows'),
    summary: asRecord(record.summary),
  };
}

function normalizeOverviewResponse(raw: unknown): MarketingOverviewResponse {
  const record = asRecord(raw);
  const timeSeries = asRecord(record.time_series);

  return {
    ...(record as Partial<MarketingOverviewResponse>),
    ok: record.ok === true,
    resource: String(record.resource ?? 'overview'),
    summary: asRecord(record.summary) as MarketingOverviewResponse['summary'],
    previous_summary: asRecord(record.previous_summary) as MarketingOverviewResponse['previous_summary'],
    ads: Array.isArray(record.ads) ? record.ads.map((row) => asRecord(row)) : [],
    adsets: Array.isArray(record.adsets) ? record.adsets.map((row) => asRecord(row)) : [],
    daily: Array.isArray(record.daily) ? record.daily.map((row) => asRecord(row)) : [],
    time_series: {
      daily: rowsFromRecord(timeSeries, 'daily'),
      monthly: rowsFromRecord(timeSeries, 'monthly'),
      weekly: rowsFromRecord(timeSeries, 'weekly'),
    },
    campaigns: Array.isArray(record.campaigns) ? record.campaigns.map((row) => asRecord(row)) : [],
    creatives: Array.isArray(record.creatives) ? record.creatives.map((row) => asRecord(row)) : [],
    forms: Array.isArray(record.forms) ? record.forms.map((row) => asRecord(row)) : [],
    regions: Array.isArray(record.regions) ? record.regions.map((row) => asRecord(row)) : [],
    alerts: Array.isArray(record.alerts) ? record.alerts.map((row) => asRecord(row)) : [],
  };
}

function normalizeDecisionMatrixResponse(raw: unknown): MarketingDecisionMatrixResponse {
  const record = asRecord(raw);

  return {
    ...(record as Partial<MarketingDecisionMatrixResponse>),
    ok: record.ok === true,
    resource: String(record.resource ?? 'decision_matrix'),
    campaigns: Array.isArray(record.campaigns) ? record.campaigns.map((row) => asRecord(row)) : [],
    adsets: Array.isArray(record.adsets) ? record.adsets.map((row) => asRecord(row)) : [],
    ads: Array.isArray(record.ads) ? record.ads.map((row) => asRecord(row)) : [],
    regions: Array.isArray(record.regions) ? record.regions.map((row) => asRecord(row)) : [],
    age_gender: Array.isArray(record.age_gender) ? record.age_gender.map((row) => asRecord(row)) : [],
    placement: Array.isArray(record.placement) ? record.placement.map((row) => asRecord(row)) : [],
  };
}

function normalizeAuditResponse(raw: unknown): MarketingAuditResponse {
  const record = asRecord(raw);

  return {
    ...(record as Partial<MarketingAuditResponse>),
    ok: record.ok === true,
    resource: String(record.resource ?? 'audit'),
    objects: Array.isArray(record.objects) ? record.objects.map((row) => asRecord(row)) : [],
    freshness: Array.isArray(record.freshness) ? record.freshness.map((row) => asRecord(row)) : [],
    duplicate_events: asRecord(record.duplicate_events),
    first_message_referral_health: asRecord(record.first_message_referral_health),
    qualified_without_first_message: asRecord(record.qualified_without_first_message),
    region_leakage: asRecord(record.region_leakage),
  };
}

function rowsFromRecord(record: Record<string, unknown>, key: string): MarketingRowsResponse['rows'] {
  const rows = record[key];
  return Array.isArray(rows) ? rows.map((row) => asRecord(row)) : [];
}

function normalizeFilterOptionsResponse(raw: unknown): MarketingFilterOptionsData {
  const record = asRecord(raw);

  return {
    ...(record as Partial<MarketingFilterOptionsData>),
    ok: record.ok === true,
    resource: String(record.resource ?? 'filter_options'),
    accounts: rowsFromRecord(record, 'accounts'),
    ads: rowsFromRecord(record, 'ads'),
    adsets: rowsFromRecord(record, 'adsets'),
    ages: rowsFromRecord(record, 'ages'),
    campaigns: rowsFromRecord(record, 'campaigns'),
    contact_sources: rowsFromRecord(record, 'contact_sources'),
    creatives: rowsFromRecord(record, 'creatives'),
    device_platforms: rowsFromRecord(record, 'device_platforms'),
    forms: rowsFromRecord(record, 'forms'),
    genders: rowsFromRecord(record, 'genders'),
    platform_positions: rowsFromRecord(record, 'platform_positions'),
    publisher_platforms: rowsFromRecord(record, 'publisher_platforms'),
    regions: rowsFromRecord(record, 'regions'),
  };
}

function emptyRowsResponse(resource: MarketingRoute): MarketingRowsResponse {
  return {
    ok: true,
    resource,
    rows: [],
  };
}

function emptyInterestRegionsResponse(): MarketingInterestRegionsResponse {
  return {
    ok: true,
    other_localities: [],
    resource: 'interest_regions',
    rows: [],
    summary: {},
  };
}

function emptyDecisionMatrixResponse(): MarketingDecisionMatrixResponse {
  return {
    ok: true,
    resource: 'decision_matrix',
    ads: [],
    adsets: [],
    age_gender: [],
    campaigns: [],
    placement: [],
    regions: [],
  };
}

function emptyAuditResponse(): MarketingAuditResponse {
  return {
    ok: true,
    resource: 'audit',
    freshness: [],
    objects: [],
  };
}

export class MarketingRepository {
  private httpRepository = new HttpRepository();

  private async getResource<T>(route: MarketingRoute, params: MarketingFetchParams): Promise<T> {
    const url = appendQuery(getHabitaMarketingResourceUrl(route), params);
    const response = await this.httpRepository.get<unknown>(url);
    return response.data as T;
  }

  async fetchOverview(params: MarketingFetchParams): Promise<MarketingOverviewResponse> {
    return normalizeOverviewResponse(await this.getResource('overview', params));
  }

  async fetchRows(route: MarketingRoute, params: MarketingFetchParams): Promise<MarketingRowsResponse> {
    return normalizeRowsResponse(await this.getResource(route, params), route);
  }

  async fetchDecisionMatrix(params: MarketingFetchParams): Promise<MarketingDecisionMatrixResponse> {
    return normalizeDecisionMatrixResponse(await this.getResource('decision-matrix', params));
  }

  async fetchAudit(params: MarketingFetchParams): Promise<MarketingAuditResponse> {
    return normalizeAuditResponse(await this.getResource('audit', params));
  }

  async fetchFilterOptions(params: MarketingFetchParams): Promise<MarketingFilterOptionsData> {
    return normalizeFilterOptionsResponse(await this.getResource(marketingRoutes.filterOptions, params));
  }

  async fetchInterestRegions(params: MarketingFetchParams): Promise<MarketingInterestRegionsResponse> {
    return normalizeInterestRegionsResponse(await this.getResource(marketingRoutes.interestRegions, params));
  }

  async fetchDashboard(params: MarketingFetchParams): Promise<MarketingDashboardData> {
    const overview = await this.fetchOverview({
      ...params,
      limit: Math.min(Number(params.limit ?? 5), 5),
    });

    return {
      ads: emptyRowsResponse('ads'),
      ageGender: emptyRowsResponse('age-gender'),
      audit: emptyAuditResponse(),
      campaigns: emptyRowsResponse('campaigns'),
      creativeRegions: emptyRowsResponse('creative-regions'),
      decisionMatrix: emptyDecisionMatrixResponse(),
      interestRegions: emptyInterestRegionsResponse(),
      overview,
      placement: emptyRowsResponse('placement'),
      regions: emptyRowsResponse('regions'),
    };
  }
}
